import { useQuery } from '@tanstack/react-query';
import type { BoardBed } from '@wodi/shared';
import { Button, LoadingBlock, Notice, Select, TextArea } from '@wodi/ui';

import { bedBoardQuery } from '../api/inpatient.ts';
import { errorText } from './bed-board-page.tsx';
import styles from './inpatient.module.css';

export const NO_BED = 'none';

/**
 * A bed waiting for a routine clean, which may be used with a reason (housekeeping plan H3).
 * A terminal clean is never offered (H4).
 */
function usableWithReason(bed: BoardBed): boolean {
  return bed.state === 'cleaning' && bed.cleaning?.kind !== 'terminal_clean';
}

/** Whether the chosen bed is waiting for cleaning, so the person must say why they use it. */
export function useBedNeedsCleaning(wardId: string | null, bedId: string): boolean {
  const board = useQuery(bedBoardQuery);
  if (bedId === NO_BED) return false;
  const bed = board.data?.wards.find((w) => w.id === wardId)?.beds.find((b) => b.id === bedId);
  return bed !== undefined && usableWithReason(bed);
}

/**
 * Ward and bed choice for admitting and moving: the facility's wards (a short, fixed list)
 * and the free beds of the chosen ward, or "No bed yet". Beds come from the live board, so
 * a bed taken a moment ago is refused by the server with a reason.
 */
export function PlacePicker({
  wardId,
  bedId,
  onWard,
  onBed,
  exceptBedId,
  cleaningReason,
  onCleaningReason,
}: {
  wardId: string | null;
  bedId: string;
  onWard: (wardId: string) => void;
  onBed: (bedId: string) => void;
  /** The bed the patient is in now, left out of the choices. */
  exceptBedId?: string | null;
  /**
   * Why a bed still waiting for cleaning is used now. Given: such beds are offered, and the
   * reason is asked for when one is chosen.
   */
  cleaningReason?: string;
  onCleaningReason?: (reason: string) => void;
}) {
  const board = useQuery(bedBoardQuery);
  if (board.isPending) return <LoadingBlock label="Loading wards and beds" rows={2} />;
  if (board.isError) {
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void board.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(board.error, "Wards and beds couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const wards = board.data.wards;
  if (wards.length === 0) {
    return (
      <Notice tone="info">
        No wards are set up at this facility. An administrator adds them in Settings.
      </Notice>
    );
  }
  const ward = wards.find((w) => w.id === wardId);
  const free = (ward?.beds ?? []).filter((b) => b.state === 'free' && b.id !== exceptBedId);
  const offerCleaning = onCleaningReason !== undefined;
  const waiting = offerCleaning
    ? (ward?.beds ?? []).filter((b) => usableWithReason(b) && b.id !== exceptBedId)
    : [];
  const chosenWaiting = waiting.some((b) => b.id === bedId);
  const bedLabel = (b: BoardBed) => (b.roomName === null ? b.label : `${b.label}, ${b.roomName}`);
  return (
    <div className={styles.row}>
      <Select
        label="Ward"
        placeholder="Choose a ward"
        options={wards.map((w) => ({
          id: w.id,
          label: `${w.name} (${String(w.counts.free)} free)`,
        }))}
        value={wardId}
        onChange={(v) => {
          onWard(String(v));
        }}
      />
      <Select
        label="Bed"
        isDisabled={ward === undefined}
        {...(ward !== undefined && free.length === 0
          ? { description: 'No free bed in this ward. The patient waits for one.' }
          : {})}
        options={[
          { id: NO_BED, label: 'No bed yet' },
          ...free.map((b) => ({ id: b.id, label: bedLabel(b) })),
          ...waiting.map((b) => ({ id: b.id, label: `${bedLabel(b)}, needs cleaning` })),
        ]}
        value={bedId}
        onChange={(v) => {
          onBed(String(v));
        }}
      />
      {chosenWaiting && onCleaningReason !== undefined ? (
        <TextArea
          label="Why use it before it is cleaned?"
          description="At least 5 characters. Recorded and audited; housekeeping's task for the bed is closed."
          value={cleaningReason ?? ''}
          onChange={onCleaningReason}
        />
      ) : null}
    </div>
  );
}
